import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import {
  AppWindow, Buildings, Broadcast, Receipt, ArrowUpRight,
  CheckCircle, Prohibit, ArrowsClockwise
} from "@phosphor-icons/react";

const STAT_DEFS = [
  { key: "applications", label: "Applications", icon: AppWindow, accent: "from-[#FF6B00] to-[#E66000]" },
  { key: "submerchants_total", label: "Sub-merchants", icon: Buildings, accent: "from-[#10B981] to-[#059669]" },
  { key: "webhook_events", label: "Webhook events", icon: Broadcast, accent: "from-slate-700 to-slate-900" },
  { key: "audit_events", label: "Audit log entries", icon: Receipt, accent: "from-slate-500 to-slate-700" },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [audit, setAudit] = useState([]);
  const [submerchants, setSubmerchants] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/stats").then(r => r.data),
      api.get("/audit-logs").then(r => r.data),
      api.get("/submerchants").then(r => r.data),
    ]).then(([s, a, sm]) => { setStats(s); setAudit(a); setSubmerchants(sm); }).catch(() => {});
  }, []);

  return (
    <div className="space-y-10">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">Overview</div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-[#0F172A] mt-1">Partner Dashboard</h1>
          <p className="mt-1.5 text-sm text-slate-500 max-w-xl">Real-time view of your OAuth applications, connected sub-merchants and recent activity.</p>
        </div>
        <Link to="/applications" data-testid="dashboard-create-app-cta"
          className="btn-primary inline-flex items-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium">
          New application <ArrowUpRight size={14} weight="bold" />
        </Link>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-stats">
        {STAT_DEFS.map(({ key, label, icon: Icon, accent }) => (
          <div key={key} className="relative overflow-hidden rounded-xl border border-[#E2E8F0] bg-white p-5">
            <div className={`absolute -top-10 -right-10 h-28 w-28 rounded-full bg-gradient-to-br ${accent} opacity-[0.08]`} />
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</div>
                <div className="mt-3 font-display text-3xl font-bold text-[#0F172A]" data-testid={`stat-${key}`}>
                  {stats ? stats[key] : "—"}
                </div>
              </div>
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-50">
                <Icon size={18} className="text-slate-700" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sub-merchants */}
        <div className="lg:col-span-2 rounded-xl border border-[#E2E8F0] bg-white">
          <div className="flex items-center justify-between border-b border-[#E2E8F0] px-5 py-4">
            <div>
              <h2 className="font-display text-lg font-semibold text-[#0F172A]">Connected sub-merchants</h2>
              <p className="text-xs text-slate-500">Status and scope across your applications.</p>
            </div>
            <Link to="/submerchants" className="text-xs font-medium text-[#FF6B00] hover:underline" data-testid="view-all-submerchants">
              View all →
            </Link>
          </div>
          {submerchants.length === 0 ? (
            <EmptyConnections />
          ) : (
            <div className="divide-y divide-[#E2E8F0]">
              {submerchants.slice(0, 5).map(sm => (
                <Link to={`/submerchants/${sm.id}`} key={sm.id} className="flex items-center justify-between px-5 py-4 hover:bg-orange-50/40 transition-colors" data-testid={`dash-sm-${sm.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm text-[#0F172A] truncate">{sm.business_name}</div>
                    <div className="font-mono text-xs text-slate-500 truncate">{sm.razorpay_account_id}</div>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <ScopeChips scopes={sm.scopes} />
                    <ModeBadge mode={sm.mode} />
                    <StatusBadge status={sm.status} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Audit activity */}
        <div className="rounded-xl border border-[#E2E8F0] bg-white">
          <div className="border-b border-[#E2E8F0] px-5 py-4">
            <h2 className="font-display text-lg font-semibold text-[#0F172A]">Recent activity</h2>
            <p className="text-xs text-slate-500">Last 8 audit events.</p>
          </div>
          {audit.length === 0 ? (
            <div className="px-5 py-8 text-sm text-slate-500">Activity will show here once you create an application.</div>
          ) : (
            <ol className="relative px-5 py-4 space-y-4">
              {audit.slice(0, 8).map((e) => (
                <li key={e.id} className="flex gap-3" data-testid={`audit-row-${e.id}`}>
                  <div className="mt-1 h-2 w-2 rounded-full bg-[#FF6B00] flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-[#0F172A] truncate">{e.message}</div>
                    <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
                      {e.event_type} · {timeAgo(e.created_at)}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyConnections() {
  return (
    <div className="px-5 py-10 text-center">
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-[#FFF3E0]">
        <Buildings size={20} className="text-[#FF6B00]" />
      </div>
      <h3 className="font-display text-base font-semibold text-[#0F172A]">No sub-merchants yet</h3>
      <p className="mt-1 text-sm text-slate-500 max-w-sm mx-auto">Create an application, build the authorize URL, and complete the OAuth flow to connect your first sub-merchant.</p>
      <Link to="/applications" className="mt-4 inline-flex items-center gap-2 rounded-md bg-[#FF6B00] px-3 py-2 text-xs font-medium text-white" data-testid="empty-create-app">
        Create application <ArrowUpRight size={12} weight="bold" />
      </Link>
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    active: { bg: "bg-[#D1FAE5]", text: "text-[#065F46]", icon: CheckCircle, label: "Active" },
    revoked: { bg: "bg-red-100", text: "text-red-700", icon: Prohibit, label: "Revoked" },
    expired: { bg: "bg-amber-100", text: "text-amber-700", icon: ArrowsClockwise, label: "Expired" },
  };
  const m = map[status] || map.active;
  const I = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full ${m.bg} ${m.text} px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider`}>
      <I size={10} weight="bold" /> {m.label}
    </span>
  );
}

export function ModeBadge({ mode }) {
  if (mode === "live") return <span className="inline-flex items-center gap-1 rounded-full bg-[#10B981] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">Live</span>;
  return <span className="inline-flex items-center gap-1 rounded-full bg-[#FFF3E0] text-[#FF6B00] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">Test</span>;
}

export function ScopeChips({ scopes = [] }) {
  return (
    <span className="hidden md:inline-flex items-center gap-1">
      {scopes.slice(0, 2).map(s => (
        <span key={s} className="rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-mono text-slate-700">{s}</span>
      ))}
      {scopes.length > 2 && <span className="text-[10px] text-slate-500">+{scopes.length - 2}</span>}
    </span>
  );
}

export function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
